import { apiUrl } from '../config'

export const MAX_DIRECT_ARTIFACT_BYTES = 20 * 1024 * 1024
const DOWNLOAD_URL_REVOKE_DELAY_MS = 60 * 1000
const MAX_ERROR_BODY_BYTES = 64 * 1024
const TICKET_PATH = /^\/reports\/artifacts\/pdf\/[A-Za-z0-9_-]{20,180}$/

const DOCUMENT_ARTIFACTS = Object.freeze({
  upd: Object.freeze([
    { alias: 'upd.pdf', label: 'PDF УПД', kind: 'pdf' },
    { alias: 'upd.xml', label: 'XML УПД', kind: 'xml' }
  ]),
  factura_advance: Object.freeze([
    { alias: 'factura.pdf', label: 'PDF счета-фактуры на аванс', kind: 'pdf' },
    { alias: 'factura.xml', label: 'XML счета-фактуры на аванс', kind: 'xml' }
  ]),
  factura_avance: Object.freeze([
    { alias: 'factura.pdf', label: 'PDF счета-фактуры на аванс', kind: 'pdf' },
    { alias: 'factura.xml', label: 'XML счета-фактуры на аванс', kind: 'xml' }
  ]),
  avance: Object.freeze([
    { alias: 'factura.pdf', label: 'PDF счета-фактуры на аванс', kind: 'pdf' },
    { alias: 'factura.xml', label: 'XML счета-фактуры на аванс', kind: 'xml' }
  ]),
  payment: Object.freeze([
    { alias: 'payment.pdf', label: 'PDF платежного поручения', kind: 'pdf' }
  ]),
  bill_to_pay: Object.freeze([
    { alias: 'bill_to_pay.pdf', label: 'PDF счета на оплату', kind: 'pdf' }
  ]),
  act_sver: Object.freeze([
    { alias: 'act_sver.pdf', label: 'PDF акта сверки', kind: 'pdf' },
    { alias: 'act_sver.xml', label: 'XML ФНС акта сверки', kind: 'xml' }
  ])
})

const STATUS_MESSAGES = Object.freeze({
  400: 'Параметры файла некорректны.',
  401: 'Сессия завершена. Войдите в систему снова.',
  403: 'Недостаточно прав для получения файла.',
  404: 'Файл или выбранная версия документа не найдены.',
  409: 'Документ изменился. Обновите страницу и повторите запрос.',
  413: 'Файл превышает допустимый размер.',
  422: 'Документ содержит данные, которые мешают сформировать файл.',
  503: 'Сервис формирования файлов временно недоступен.',
  504: 'Формирование файла заняло слишком много времени.'
})

const CODE_MESSAGES = Object.freeze({
  artifact_unavailable: 'Сервис формирования файлов временно недоступен.',
  artifact_timeout: 'Формирование файла заняло слишком много времени.',
  artifact_size_limit: 'Файл превышает допустимый размер.',
  form_version_not_found: 'Для даты документа не найдена подходящая версия формы.',
  stream_download_unsupported: 'Большой отчет нужно скачать в браузере с поддержкой потокового сохранения файлов.',
  invalid_ticket: 'Ссылка на большой отчет недействительна.',
  popup_blocked: 'Браузер заблокировал новое окно. Разрешите всплывающие окна или скачайте файл.'
})

export class ArtifactRequestError extends Error {
  constructor({ code = 'artifact_failed', message = '', status = 0, details = null, diagnostics = [] } = {}) {
    super(message || CODE_MESSAGES[code] || STATUS_MESSAGES[status] || 'Не удалось получить файл.')
    this.name = 'ArtifactRequestError'
    this.code = code
    this.status = status
    this.details = details
    this.diagnostics = diagnostics
  }
}

function credentials(connection) {
  const token = connection && connection.token
  const tokenUuid = connection && connection.token_uuid
  if (!token || !tokenUuid) {
    throw new ArtifactRequestError({ code: 'unauthorized', status: 401 })
  }
  return {
    'X-Auth-Token': token,
    'X-Auth-Token-Uuid': tokenUuid
  }
}

function boundedText(value, max = 500) {
  return Array.from(String(value || ''))
    .map((character) => {
      const code = character.charCodeAt(0)
      return code <= 31 || code === 127 ? ' ' : character
    })
    .join('')
    .trim()
    .slice(0, max)
}

export function safeArtifactFilename(value, fallback = 'document') {
  const normalized = boundedText(value, 300)
    .replace(/[\\/]/g, '_')
    .replace(/^\.+/, '')
    .replace(/\s+/g, ' ')
    .trim()
  return (normalized || fallback).slice(0, 180)
}

export function filenameFromDisposition(value, fallback = 'document') {
  const header = String(value || '')
  const encoded = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (encoded) {
    try {
      return safeArtifactFilename(decodeURIComponent(encoded[1]), fallback)
    } catch (_) {
      // Fall through to the ASCII filename instead of exposing a malformed value.
    }
  }
  const quoted = header.match(/filename\s*=\s*"([^"]+)"/i)
  const plain = header.match(/filename\s*=\s*([^;]+)/i)
  return safeArtifactFilename((quoted && quoted[1]) || (plain && plain[1]), fallback)
}

function metadataFromResponse(response, fallbackFilename) {
  return {
    filename: filenameFromDisposition(response.headers.get('Content-Disposition'), fallbackFilename),
    contentType: boundedText(response.headers.get('Content-Type'), 120),
    contentLength: Number(response.headers.get('Content-Length') || 0),
    formRevision: boundedText(response.headers.get('X-SIDERP-Form-Revision'), 160),
    formVersion: boundedText(response.headers.get('X-SIDERP-Form-Version'), 160),
    artifactHash: boundedText(response.headers.get('X-SIDERP-Artifact-Hash'), 64),
    sourceHash: boundedText(response.headers.get('X-SIDERP-Source-Hash'), 64),
    selectionMode: boundedText(response.headers.get('X-SIDERP-Selection-Mode'), 32),
    signatureStatus: boundedText(response.headers.get('X-SIDERP-Signature-Status'), 80)
  }
}

function diagnosticList(payload) {
  const details = payload && payload.details
  const values = Array.isArray(details)
    ? details
    : (Array.isArray(details && details.diagnostics) ? details.diagnostics : [])
  return values.slice(0, 50).map((item) => ({
    code: boundedText(item && item.code, 120),
    message: boundedText(item && item.message, 500),
    fieldPath: boundedText(item && (item.field_path || item.field), 180),
    lineIndex: Number.isInteger(item && item.line_index) ? item.line_index : null,
    expected: boundedText(item && item.expected, 180),
    actual: boundedText(item && item.actual, 180)
  }))
}

async function responseError(response) {
  let payload = {}
  const length = Number(response.headers.get('Content-Length') || 0)
  if (!length || length <= MAX_ERROR_BODY_BYTES) {
    try {
      payload = await response.json()
    } catch (_) {
      payload = {}
    }
  }
  const code = boundedText(payload.error, 120) || `http_${response.status}`
  throw new ArtifactRequestError({
    code,
    status: response.status,
    message: boundedText(payload.message, 500) || CODE_MESSAGES[code] || STATUS_MESSAGES[response.status],
    details: payload.details || null,
    diagnostics: diagnosticList(payload)
  })
}

function ensureDirectSize(response, limit = MAX_DIRECT_ARTIFACT_BYTES) {
  const length = Number(response.headers.get('Content-Length') || 0)
  if (Number.isFinite(length) && length > limit) {
    throw new ArtifactRequestError({ code: 'artifact_size_limit', status: 413 })
  }
}

async function readDirectBlob(response, limit = MAX_DIRECT_ARTIFACT_BYTES) {
  ensureDirectSize(response, limit)
  if (!response.body || typeof response.body.getReader !== 'function') {
    const blob = await response.blob()
    if (blob.size > limit) throw new ArtifactRequestError({ code: 'artifact_size_limit', status: 413 })
    if (!blob.size) throw new ArtifactRequestError({ code: 'empty_artifact', message: 'Сервер вернул пустой файл.' })
    return blob
  }

  const reader = response.body.getReader()
  const chunks = []
  let size = 0
  let done = false
  try {
    while (!done) {
      const next = await reader.read()
      done = next.done
      if (done) break
      size += next.value.byteLength
      if (size > limit) {
        await reader.cancel().catch(() => undefined)
        throw new ArtifactRequestError({ code: 'artifact_size_limit', status: 413 })
      }
      chunks.push(next.value)
    }
  } finally {
    reader.releaseLock()
  }
  if (!size) throw new ArtifactRequestError({ code: 'empty_artifact', message: 'Сервер вернул пустой файл.' })
  return new Blob(chunks, { type: response.headers.get('Content-Type') || '' })
}

async function directArtifactResult(response, fallbackFilename, limit = MAX_DIRECT_ARTIFACT_BYTES) {
  const metadata = metadataFromResponse(response, fallbackFilename)
  const blob = await readDirectBlob(response, limit)
  metadata.contentLength = blob.size
  return { delivery: 'blob', blob, metadata }
}

async function fetchWithAuth(connection, url, options = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...credentials(connection),
      ...(options.headers || {})
    }
  })
  if (!response.ok && response.status !== 202) await responseError(response)
  return response
}

export function documentArtifactsFor(type) {
  return (DOCUMENT_ARTIFACTS[type] || []).map((item) => ({ ...item }))
}

export function documentArtifactUrl({ documentId, orgId, historyId, artifactName, formVersion }) {
  const params = new URLSearchParams({ org_id: String(orgId) })
  if (historyId !== undefined && historyId !== null && historyId !== '' && historyId !== 'last') {
    params.set('history_id', String(historyId))
  }
  if (formVersion === 'current') params.set('form_version', 'current')
  return `${apiUrl}/documents/${encodeURIComponent(String(documentId))}/artifacts/${encodeURIComponent(artifactName)}?${params}`
}

export async function fetchDocumentArtifact({ connection, signal, ...request }) {
  const artifactName = request.artifactName
  const fallback = artifactName || 'document'
  const response = await fetchWithAuth(connection, documentArtifactUrl(request), { method: 'GET', signal })
  return directArtifactResult(response, fallback)
}

function ticketFromPayload(payload) {
  const path = String(payload && payload.download_path || '')
  if (!TICKET_PATH.test(path)) {
    throw new ArtifactRequestError({ code: 'invalid_ticket' })
  }
  return {
    delivery: 'one_time_ticket',
    downloadPath: path,
    filename: safeArtifactFilename(payload.filename, 'report.pdf'),
    expiresAt: boundedText(payload.expires_at, 80),
    formVersion: boundedText(payload.form_version, 160),
    artifactHash: boundedText(payload.artifact_hash, 64),
    sourceHash: boundedText(payload.source_hash, 64)
  }
}

export async function requestReportArtifact({ connection, payload, signal, directLimit = MAX_DIRECT_ARTIFACT_BYTES }) {
  const response = await fetchWithAuth(connection, `${apiUrl}/reports/artifacts/pdf`, {
    method: 'POST',
    signal,
    body: JSON.stringify(payload)
  })
  if (response.status === 202) return ticketFromPayload(await response.json())
  return directArtifactResult(response, 'report.pdf', directLimit)
}

export async function requestTaxXmlArtifact({ connection, payload, signal }) {
  let response
  try {
    response = await fetchWithAuth(connection, `${apiUrl}/reports/artifacts/fns-xml`, {
      method: 'POST',
      signal,
      body: JSON.stringify(payload)
    })
  } catch (error) {
    if (error instanceof ArtifactRequestError) {
      const raw = Array.isArray(error.details) ? error.details : error.details?.diagnostics || []
      error.diagnostics = error.diagnostics.map((item, index) => ({
        ...item,
        documentId: raw[index]?.document_id || null
      }))
    }
    throw error
  }
  const contentType = String(response.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase()
  if (!['application/xml', 'text/xml', 'application/zip'].includes(contentType)) {
    throw new ArtifactRequestError({ code: 'invalid_artifact', message: 'Сервер вернул неподдерживаемый формат файла.' })
  }
  if (!/filename\*?\s*=/i.test(response.headers.get('Content-Disposition') || '')) {
    throw new ArtifactRequestError({ code: 'invalid_artifact_filename', message: 'Сервер не передал имя файла ФНС. Повторите выгрузку.' })
  }
  return directArtifactResult(response, 'report.xml')
}

export async function fetchPersistedReportArtifact({ connection, documentId, orgId, historyId, formVersion, signal }) {
  const params = new URLSearchParams({ org_id: String(orgId) })
  if (historyId && historyId !== 'last') params.set('history_id', String(historyId))
  if (formVersion === 'current') params.set('form_version', 'current')
  const response = await fetchWithAuth(
    connection,
    `${apiUrl}/reports/${encodeURIComponent(String(documentId))}/artifacts/report.pdf?${params}`,
    { method: 'GET', signal }
  )
  return directArtifactResult(response, 'report.pdf')
}

export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = safeArtifactFilename(filename)
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_REVOKE_DELAY_MS)
}

async function streamResponse(response, handle) {
  if (!response.body || !handle || typeof handle.createWritable !== 'function') {
    throw new ArtifactRequestError({ code: 'stream_download_unsupported' })
  }
  const writable = await handle.createWritable()
  try {
    await response.body.pipeTo(writable)
  } catch (error) {
    if (typeof writable.abort === 'function') {
      await writable.abort().catch(() => undefined)
    }
    throw error
  }
}

export async function chooseReportFile(suggestedName = 'report.pdf') {
  if (typeof window.showSaveFilePicker !== 'function') return null
  try {
    return await window.showSaveFilePicker({
      suggestedName: safeArtifactFilename(suggestedName, 'report.pdf'),
      types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
    })
  } catch (error) {
    if (error && error.name === 'AbortError') throw error
    throw new ArtifactRequestError({ code: 'stream_download_unsupported' })
  }
}

async function ticketResponse({ connection, ticket, signal }) {
  return fetchWithAuth(connection, `${apiUrl}${ticket.downloadPath}`, { method: 'GET', signal })
}

export async function saveReportArtifact({ connection, payload, suggestedName, signal }) {
  const handle = await chooseReportFile(suggestedName)
  const request = async () => {
    const response = await fetchWithAuth(connection, `${apiUrl}/reports/artifacts/pdf`, {
      method: 'POST',
      signal,
      body: JSON.stringify(payload)
    })
    if (response.status !== 202) {
      if (handle) {
        await streamResponse(response, handle)
        return { delivery: 'stream', metadata: metadataFromResponse(response, suggestedName) }
      }
      const result = await directArtifactResult(response, suggestedName)
      triggerBlobDownload(result.blob, result.metadata.filename)
      return result
    }
    return ticketFromPayload(await response.json())
  }

  let result = await request()
  if (result.delivery !== 'one_time_ticket') return result
  if (!handle) throw new ArtifactRequestError({ code: 'stream_download_unsupported' })

  let response
  try {
    response = await ticketResponse({ connection, ticket: result, signal })
  } catch (error) {
    if (!(error instanceof ArtifactRequestError) || error.status !== 404) throw error
    result = await request()
    if (result.delivery !== 'one_time_ticket') return result
    response = await ticketResponse({ connection, ticket: result, signal })
  }
  await streamResponse(response, handle)
  return {
    delivery: 'one_time_ticket',
    metadata: {
      ...metadataFromResponse(response, result.filename),
      formVersion: result.formVersion,
      artifactHash: result.artifactHash,
      sourceHash: result.sourceHash,
      expiresAt: result.expiresAt
    }
  }
}

export function artifactErrorMessage(error) {
  if (error && error.name === 'AbortError') return ''
  return error instanceof ArtifactRequestError
    ? error.message
    : 'Не удалось получить файл.'
}
